// react_frontend/src/Components/Modules/PurchaseSetting/AddPurchase.js
import React, { useState, useEffect, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";

const AddPurchase = () => {
  const { categories, items, startCategoryPolling, startItemPolling } = useContext(MotherContext);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Each row: { category_id, item_id, quantity, product_unit, unit_price, price, note }
  const [rows, setRows] = useState([
    {
      category_id: "",
      item_id: "",
      quantity: "",
      product_unit: "",
      unit_price: "",
      price: 0,
      note: "",
    },
  ]);

  // Start polling for categories and items
  useEffect(() => {
    startCategoryPolling();
    startItemPolling();
  }, [startCategoryPolling, startItemPolling]);

  // Get items for a category (from context items)
  const getItemsByCategory = (categoryId) => {
    return items.filter(item => item.category_id == categoryId && item.item_status === 1);
  };

  // When category changes in a row, update that row's category and reset item
  const handleCategoryChange = (index, categoryId) => {
    const updatedRows = [...rows];
    updatedRows[index].category_id = categoryId;
    updatedRows[index].item_id = "";
    updatedRows[index].product_unit = ""; // reset selected item
    setRows(updatedRows);
  };

  // Handle input changes in rows
  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;

    // Auto-load unit when item is selected
    if (field === "item_id" && value) {
      const selectedItem = items.find(item => item.id == value);
      if (selectedItem && selectedItem.product_unit) {
        updatedRows[index].product_unit = selectedItem.product_unit;
      }
    }

    // Update price = quantity * unit_price
    if (field === "quantity" || field === "unit_price") {
      const qty = Number(updatedRows[index].quantity) || 0;
      const unitPrice = Number(updatedRows[index].unit_price) || 0;
      updatedRows[index].price = qty * unitPrice;
    }

    setRows(updatedRows);
  };

  // Add new empty row
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        category_id: "",
        item_id: "",
        quantity: "",
        product_unit: "",
        unit_price: "",
        price: 0,
        note: "",
      },
    ]);
  };

  // Remove a row by index
  const handleRemoveRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData(e.target);
    AxiosAuthInstance.post("purchases", formData)
      .then((response) => {
        if (response.data.status === true) {
          navigate("/purchase_list");
        }
      })
      .catch((error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <Breadcrumb title={"Add Purchase"} />
      <div className="row">
        <div className="col-md-12">
          <div className="card custom-bg text-white">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="fs-6">Add Purchase</h4>
                <Link to="/purchase_list" className="text-white text-decoration-none">
                  <button className="btn btn-sm custom-bg text-white">
                    <i className="fa-solid fa-list"></i> Purchase List
                  </button>
                </Link>
              </div>
            </div>
            <div className="card-body bg-white overflow-auto">
              <form onSubmit={handleSubmit} method="post">
                {/* General purchase info */}
                <div className="row">
                  <div className="col-lg-2 col-sm-4">
                    <label htmlFor="receipt_no" className="text-dark">Receipt no</label>
                    <input
                      type="text"
                      name="receipt_no"
                      className={`form-control mb-4 ${errors.receipt_no ? "is-invalid" : ""}`}
                      placeholder="Receipt No"
                    />
                    <p className="text-danger"><small>{errors.receipt_no?.[0]}</small></p>
                  </div>
                  <div className="col-lg-3 col-sm-6">
                    <label htmlFor="organization_name" className="text-dark">Organization name</label>
                    <input
                      type="text"
                      name="organization_name"
                      className={`form-control mb-4 ${errors.organization_name ? "is-invalid" : ""}`}
                      placeholder="Organization name"
                    />
                    <p className="text-danger"><small>{errors.organization_name?.[0]}</small></p>
                  </div>
                  <div className="col-lg-3 col-sm-6">
                    <label htmlFor="organization_address" className="text-dark">Organization address</label>
                    <input
                      type="text"
                      name="organization_address"
                      className={`form-control mb-4 ${errors.organization_address ? "is-invalid" : ""}`}
                      placeholder="Organization address"
                    />
                    <p className="text-danger"><small>{errors.organization_address?.[0]}</small></p>
                  </div>
                  <div className="col-lg-2 col-sm-4">
                    <label htmlFor="purchase_date" className="text-dark">Purchase Date</label>
                    <input
                      type="date"
                      name="purchase_date"
                      className={`form-control mb-4 ${errors.purchase_date ? "is-invalid" : ""}`}
                      placeholder="Enter Purchase Date"
                    />
                    <p className="text-danger"><small>{errors.purchase_date?.[0]}</small></p>
                  </div>
                </div>

                {/* Rows of purchase items */}
                {rows.map((row, index) => (
                  <div className="row custom-border align-items-end mb-3" key={index}>
                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Category</label>
                      <select
                        name="category_id[]"
                        value={row.category_id}
                        onChange={(e) => handleCategoryChange(index, e.target.value)}
                        className={`form-select mb-2 ${errors["category_id." + index] ? "is-invalid" : ""}`}
                      >
                        <option value="">Select Category</option>
                        {categories
                          .filter(cat => cat.category_status === 1) // ✅ Only active categories
                          .map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                          ))}
                      </select>
                      <p className="text-danger"><small>{errors["category_id." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Item</label>
                      <select
                        name="item_id[]"
                        value={row.item_id}
                        onChange={(e) => handleRowChange(index, "item_id", e.target.value)}
                        className={`form-select mb-2 ${errors["item_id." + index] ? "is-invalid" : ""}`}
                      >
                        <option disabled value="">Select Item</option>
                        {row.category_id && getItemsByCategory(row.category_id).map((item) => (
                          <option key={item.id} value={item.id}>{item.item_name}</option>
                        ))}
                      </select>
                      <p className="text-danger"><small>{errors["item_id." + index]?.[0]}</small></p>
                    </div>

                    {/* ... rest of the fields (quantity, unit_price, etc.) remain unchanged ... */}
                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Quantity</label>
                      <input
                        type="number"
                        name="quantity[]"
                        value={row.quantity}
                        onChange={(e) => handleRowChange(index, "quantity", e.target.value)}
                        className={`form-control mb-2 ${errors["quantity." + index] ? "is-invalid" : ""}`}
                        placeholder="Quantity"
                      />
                      <p className="text-danger"><small>{errors["quantity." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Product Unit</label>
                      <input
                        type="text"
                        name="product_unit[]"
                        value={row.product_unit}
                        readOnly
                        className={`form-control mb-2 ${errors["product_unit." + index] ? "is-invalid" : ""}`}
                        placeholder="Auto-loaded unit"
                        style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                      />
                      <p className="text-danger"><small>{errors["product_unit." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Unit Price</label>
                      <input
                        type="number"
                        name="unit_price[]"
                        value={row.unit_price}
                        onChange={(e) => handleRowChange(index, "unit_price", e.target.value)}
                        className={`form-control mb-2 ${errors["unit_price." + index] ? "is-invalid" : ""}`}
                        placeholder="Unit price"
                      />
                      <p className="text-danger"><small>{errors["unit_price." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Price</label>
                      <input
                        type="number"
                        name="price[]"
                        value={row.price}
                        readOnly
                        className={`form-control mb-2 ${errors["price." + index] ? "is-invalid" : ""}`}
                        placeholder="Price"
                      />
                      <p className="text-danger"><small>{errors["price." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-2 col-md-4 col-sm-6 col-12">
                      <label className="text-dark fw-bold">Note</label>
                      <input
                        type="text"
                        name="note[]"
                        value={row.note}
                        onChange={(e) => handleRowChange(index, "note", e.target.value)}
                        className={`form-control mb-2 ${errors["note." + index] ? "is-invalid" : ""}`}
                        placeholder="Enter Note if any?"
                      />
                      <p className="text-danger"><small>{errors["note." + index]?.[0]}</small></p>
                    </div>

                    <div className="col-xl-1 col-md-2 col-sm-2 col-6 d-flex align-items-center pb-2">
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveRow(index)}
                          title="Remove Row"
                        >
                          ✘
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="row">
                  <div className="col-xl-11 col-sm-10 col-8 my-4">
                    <button
                      type="submit"
                      className="btn custom-bg text-white btn-md"
                      disabled={isLoading}
                      dangerouslySetInnerHTML={{
                        __html: isLoading
                          ? '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...'
                          : "Add Purchase",
                      }}
                    />
                  </div>
                  <div className="col-xl-1 col-sm-2 col-4 d-flex align-items-center justify-content-end pb-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary custom-bg"
                      onClick={handleAddRow}
                      title="Add Row"
                    >
                      ✚
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPurchase;